import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Plus, MoreVertical, Pencil, Trash2, Car, Users, Clock, Package,
  CheckCircle2, Circle, X, FileText, Play, Pause, Square, Timer, Download, Eye, UserCog,
  Archive, ArchiveRestore
} from "lucide-react";
import { ClipboardList } from "lucide-react";
import { getStatusBadge, formatMinutes } from "./orderHelpers";

export const OrderCard = ({ order, totals, isHighlighted, user, actions }) => {
  const {
    openOrderDetail, handleStatusChange, handleArchive, handleUnarchive,
    handleEdit, handleDelete,
    handleToggleTask, openEditTaskDialog, handleDeleteTask,
    onAddTask, startTimer, togglePause, stopTimer, deleteTimeEntry,
    openManualTimeEntry, handleToggleArbeit, handleAbschlusskontrolleChange,
    getActiveTimeEntry, navigate
  } = actions;
  const isArchived = !!order.archived;
  const isErledigt = order.status === "erledigt";

  return (
    <Card className={`border shadow-sm card-hover border-l-4 transition-all duration-300 ${
      order.status === 'offen' ? 'border-l-blue-500' :
      order.status === 'in_bearbeitung' ? 'border-l-orange-500' :
      'border-l-green-500'
    } ${isHighlighted ? 'ring-2 ring-primary ring-offset-2 bg-primary/5' : ''}`} data-testid={`order-card-${order.id}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="space-y-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => openOrderDetail(order)}>
            <CardTitle className="text-base lg:text-lg font-semibold truncate" style={{ fontFamily: 'Barlow Condensed' }}>
              {order.description || "Auftrag"}
            </CardTitle>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                {order.vehicle_id
                  ? <Car className="w-4 h-4 flex-shrink-0" />
                  : <ClipboardList className="w-4 h-4 flex-shrink-0" />}
                <span className="truncate">{order.vehicle_info}</span>
              </span>
              <span className="flex items-center gap-1"><Users className="w-4 h-4 flex-shrink-0" /><span className="truncate">{order.customer_name}</span></span>
              {order.assigned_to_names && order.assigned_to_names.length > 0 && (
                <span className="flex items-center gap-1"><UserCog className="w-4 h-4 flex-shrink-0 text-primary" /><span className="truncate">{order.assigned_to_names.join(", ")}</span></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {getStatusBadge(order.status)}
            <Button variant="outline" size="sm" onClick={() => openOrderDetail(order)} className="hidden sm:flex gap-1" data-testid={`view-order-${order.id}`}>
              <Eye className="w-4 h-4" /> Anzeigen
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid={`order-menu-${order.id}`}><MoreVertical className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openOrderDetail(order)} data-testid={`view-detail-${order.id}`}><Eye className="w-4 h-4 mr-2" />Details anzeigen</DropdownMenuItem>
                {isArchived ? (
                  <>
                    <DropdownMenuItem onClick={() => navigate(`/pdf-export?order=${order.id}`)} data-testid={`pdf-order-${order.id}`}><Download className="w-4 h-4 mr-2" />Arbeitskarte PDF</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleUnarchive(order.id)} data-testid={`unarchive-order-${order.id}`}><ArchiveRestore className="w-4 h-4 mr-2 text-blue-600" />Wiederherstellen</DropdownMenuItem>
                    {user?.role === "admin" && (
                      <DropdownMenuItem onClick={() => handleDelete(order.id)} className="text-destructive" data-testid={`delete-order-${order.id}`}><Trash2 className="w-4 h-4 mr-2" />Löschen</DropdownMenuItem>
                    )}
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => handleStatusChange(order.id, "offen")}><Circle className="w-4 h-4 mr-2 text-blue-500" />Offen</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleStatusChange(order.id, "in_bearbeitung")}><Clock className="w-4 h-4 mr-2 text-orange-500" />In Bearbeitung</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleStatusChange(order.id, "erledigt")}><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />Erledigt</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleEdit(order)} data-testid={`edit-order-${order.id}`}><Pencil className="w-4 h-4 mr-2" />Bearbeiten</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/pdf-export?order=${order.id}`)} data-testid={`pdf-order-${order.id}`}><Download className="w-4 h-4 mr-2" />Arbeitskarte PDF</DropdownMenuItem>
                    {isErledigt && (
                      <DropdownMenuItem onClick={() => handleArchive(order.id)} data-testid={`archive-order-${order.id}`}><Archive className="w-4 h-4 mr-2 text-blue-600" />Archivieren</DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleDelete(order.id)} className="text-destructive" data-testid={`delete-order-${order.id}`}><Trash2 className="w-4 h-4 mr-2" />Löschen</DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Stats Row */}
        <div className="flex flex-wrap gap-3 lg:gap-6 mb-4 text-xs lg:text-sm">
          <div className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-muted-foreground" /><span>{totals.completedTasks}/{totals.totalTasks} Aufgaben</span></div>
          <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" /><span>{Math.floor(totals.totalTime / 60)}h {totals.totalTime % 60}min</span></div>
          <div className="flex items-center gap-2"><Package className="w-4 h-4 text-muted-foreground" /><span>{totals.totalCost.toFixed(2)} €</span></div>
        </div>

        <Accordion type="multiple" className="w-full">
          {/* Tasks Accordion */}
          <AccordionItem value="tasks" className="border-none">
            <AccordionTrigger className="py-2 hover:no-underline" data-testid={`tasks-accordion-${order.id}`}>
              <span className="text-sm font-medium">Aufgaben anzeigen</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2 pt-2">
                {order.tasks && order.tasks.length > 0 ? (
                  order.tasks.map((task) => (
                    <div key={task.id} className={`p-3 rounded-sm border ${task.completed ? 'bg-green-50 border-green-200' : 'bg-white'}`} data-testid={`task-item-${task.id}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox checked={task.completed} onCheckedChange={() => handleToggleTask(order.id, task.id, task.completed)} data-testid={`task-checkbox-${task.id}`} className="mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}>{task.description}</p>
                          {task.time_minutes > 0 && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground mt-1"><Clock className="w-3 h-3" />{task.time_minutes} min</span>
                          )}
                          {task.materialien && task.materialien.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {task.materialien.map((m, idx) => (
                                <div key={idx} className="text-xs bg-slate-100 p-1.5 rounded flex items-center gap-2 flex-wrap">
                                  <Package className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                  <span className="font-medium">{m.bezeichnung}</span>
                                  {m.menge && <span className="text-muted-foreground">({m.menge})</span>}
                                  {m.artikelnummer && <span className="text-muted-foreground">Art.Nr: {m.artikelnummer}</span>}
                                  {m.kosten > 0 && <span className="text-green-700 ml-auto">{parseFloat(m.kosten).toFixed(2)} €</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {(!task.materialien || task.materialien.length === 0) && task.material && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                              <Package className="w-3 h-3" />{task.material} ({task.material_cost?.toFixed(2)} €)
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEditTaskDialog(order, task)} data-testid={`edit-task-${task.id}`}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => handleDeleteTask(order.id, task.id)} data-testid={`delete-task-${task.id}`}><X className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Keine Aufgaben vorhanden</p>
                )}
                <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => onAddTask(order)} data-testid={`add-task-button-${order.id}`}>
                  <Plus className="w-4 h-4 mr-2" /> Aufgabe hinzufügen
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Zeiterfassung Accordion */}
          <AccordionItem value="zeiterfassung" className="border-none border-t">
            <AccordionTrigger className="py-2 hover:no-underline" data-testid={`time-accordion-${order.id}`}>
              <span className="text-sm font-medium flex items-center gap-2">
                <Timer className="w-4 h-4" /> Zeiterfassung
                {order.is_timer_running && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
                {order.total_work_minutes > 0 && <span className="text-muted-foreground font-normal">({formatMinutes(order.total_work_minutes)})</span>}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pt-2">
                <div className="flex gap-2 flex-wrap">
                  {!order.is_timer_running ? (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => startTimer(order.id)} data-testid={`start-timer-${order.id}`}>
                      <Play className="w-4 h-4 mr-1" /> Arbeit starten
                    </Button>
                  ) : (
                    <>
                      <Button size="sm" variant={getActiveTimeEntry(order)?.pause_start ? "default" : "outline"} className={getActiveTimeEntry(order)?.pause_start ? "bg-yellow-600 hover:bg-yellow-700" : ""} onClick={() => togglePause(order.id)} data-testid={`pause-timer-${order.id}`}>
                        <Pause className="w-4 h-4 mr-1" /> {getActiveTimeEntry(order)?.pause_start ? "Weiterarbeiten" : "Pause"}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => stopTimer(order.id)} data-testid={`stop-timer-${order.id}`}>
                        <Square className="w-4 h-4 mr-1" /> Arbeit beenden
                      </Button>
                    </>
                  )}
                  {user?.role === "admin" && (
                    <Button size="sm" variant="outline" onClick={() => openManualTimeEntry(order.id)} data-testid={`manual-time-${order.id}`}>
                      <Clock className="w-4 h-4 mr-1" /> Nachtrag
                    </Button>
                  )}
                </div>
                {order.is_timer_running && getActiveTimeEntry(order) && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center gap-2 text-green-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="font-medium">Läuft seit {getActiveTimeEntry(order).start_time} Uhr</span>
                      {getActiveTimeEntry(order).pause_start && <span className="text-yellow-600 text-sm">(Pause)</span>}
                    </div>
                  </div>
                )}
                {order.time_entries && order.time_entries.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Zeiteinträge</p>
                    {order.time_entries.filter(e => !e.is_active).map((entry) => {
                      const startParts = entry.start_time?.split(":") || [0,0];
                      const endParts = entry.end_time?.split(":") || [0,0];
                      const startMins = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
                      const endMins = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
                      const workMins = endMins - startMins - (entry.pause_minutes || 0);
                      return (
                        <div key={entry.id} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                          <div>
                            <span className="font-medium">{entry.date}</span>
                            <span className="text-muted-foreground ml-2">{entry.start_time} - {entry.end_time}</span>
                            {entry.pause_minutes > 0 && <span className="text-muted-foreground ml-2">(Pause: {entry.pause_minutes}min)</span>}
                            {entry.is_manual && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Nachtrag</span>}
                            {entry.notes && <span className="ml-2 text-xs text-muted-foreground italic">{entry.notes}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-green-700">{formatMinutes(workMins > 0 ? workMins : 0)}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => deleteTimeEntry(order.id, entry.id)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {order.total_work_minutes > 0 && (
                  <div className="p-3 bg-slate-100 rounded-md">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Gesamtarbeitszeit:</span>
                      <span className="text-lg font-bold text-primary">{formatMinutes(order.total_work_minutes)}</span>
                    </div>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Arbeitskarte Accordion */}
          {order.arbeitskarte && (
            <AccordionItem value="arbeitskarte" className="border-none border-t">
              <AccordionTrigger className="py-2 hover:no-underline" data-testid={`arbeitskarte-accordion-${order.id}`}>
                <span className="text-sm font-medium flex items-center gap-2"><FileText className="w-4 h-4" /> Arbeitskarte anzeigen</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2 bg-slate-50 p-4 rounded-md">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Kunde</p>
                      <p className="font-medium">{order.arbeitskarte.kundenname}</p>
                      <p className="text-sm text-muted-foreground">{order.arbeitskarte.adresse}</p>
                      <p className="text-sm text-muted-foreground">{order.arbeitskarte.telefon_email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Fahrzeug</p>
                      <p className="font-medium">{order.arbeitskarte.hersteller} {order.arbeitskarte.fahrzeugtyp_modell}</p>
                      <p className="text-sm text-muted-foreground">Kennzeichen: {order.arbeitskarte.kennzeichen}</p>
                      <p className="text-sm text-muted-foreground">Baujahr: {order.arbeitskarte.baujahr}</p>
                      {order.arbeitskarte.vin && <p className="text-sm text-muted-foreground">VIN: {order.arbeitskarte.vin}</p>}
                      {order.arbeitskarte.kilometerstand && <p className="text-sm text-muted-foreground">KM-Stand: {order.arbeitskarte.kilometerstand}</p>}
                    </div>
                  </div>
                  {order.arbeitskarte.arbeiten && order.arbeitskarte.arbeiten.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Durchgeführte Arbeiten</p>
                      <div className="space-y-2">
                        {order.arbeitskarte.arbeiten.map((arbeit, idx) => (
                          <div key={idx} className={`p-3 rounded border ${arbeit.erledigt ? 'bg-green-50 border-green-200' : 'bg-white'}`}>
                            <div className="flex items-start gap-2">
                              <Checkbox checked={arbeit.erledigt || false} onCheckedChange={() => handleToggleArbeit(order.id, idx, arbeit.erledigt)} data-testid={`arbeit-checkbox-${order.id}-${idx}`} className="mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium text-sm ${arbeit.erledigt ? 'line-through text-muted-foreground' : ''}`}>{arbeit.beschreibung}</p>
                                {arbeit.time_minutes > 0 && (
                                  <span className="flex items-center gap-1 text-xs text-muted-foreground mt-1"><Clock className="w-3 h-3" />{arbeit.time_minutes} min</span>
                                )}
                                {arbeit.materialien && arbeit.materialien.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    <p className="text-xs text-muted-foreground">Benötigte Materialien:</p>
                                    {arbeit.materialien.map((m, mIdx) => (
                                      <div key={mIdx} className="text-xs bg-slate-100 p-1.5 rounded flex items-center gap-2 flex-wrap">
                                        <Package className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                        <span className="font-medium">{m.bezeichnung}</span>
                                        {m.menge && <span className="text-muted-foreground">({m.menge})</span>}
                                        {m.artikelnummer && <span className="text-muted-foreground">Art.Nr: {m.artikelnummer}</span>}
                                        {m.anzahl && <span className="text-muted-foreground">{m.anzahl} {m.einheit}</span>}
                                        {m.einzelpreis > 0 && <span className="text-muted-foreground">à {parseFloat(m.einzelpreis).toFixed(2)} €</span>}
                                        {m.kosten > 0 && <span className="text-green-700 ml-auto">{parseFloat(m.kosten).toFixed(2)} €</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {order.arbeitskarte.abschlusskontrolle && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Abschlusskontrolle</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        {[
                          ['sichtpruefung_unterboden', 'Sichtprüfung Unterboden'],
                          ['bremsanlage_geprueft', 'Bremsanlage geprüft'],
                          ['reifendruck_kontrolliert', 'Reifendruck kontrolliert'],
                          ['oelstand_kontrolliert', 'Ölstand kontrolliert'],
                          ['kuehlmittel_kontrolliert', 'Kühlmittel kontrolliert'],
                          ['probefahrt_durchgefuehrt', 'Probefahrt durchgeführt'],
                          ['funktionspruefung_elektrisch', 'Elektrische Funktionsprüfung'],
                        ].map(([field, label]) => (
                          <div key={field} className="flex items-center gap-2">
                            <Checkbox
                              checked={order.arbeitskarte.abschlusskontrolle[field] || false}
                              onCheckedChange={(checked) => handleAbschlusskontrolleChange(order.id, field, checked)}
                              data-testid={`ak-check-${field.replace(/_/g, '-')}-${order.id}`}
                            />
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {order.arbeitskarte.bemerkungen && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Bemerkungen</p>
                      <p className="text-sm bg-white p-2 rounded">{order.arbeitskarte.bemerkungen}</p>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground pt-2 border-t">
                    Erstellt am: {order.arbeitskarte.datum}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </CardContent>
    </Card>
  );
};
